import { shallowRef, toRaw } from 'vue'

import { getCustomIconUrl } from '@/components/common/google-map/google-map-markers'
import Markers from './google-map-markers'
import EventBus from '@/components/mixins/event-bus-mixin'
import _ from 'lodash'

const TILE_SIZE = 256
const PLAYERS_PREVIEW_MODAL_ID = 'mapPlayersPreviewModal'
const PLAYERS_PREVIEW_MODAL_CLOSE_ID = 'mapPlayersPreviewModalClose'
const MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID = 'mapMarkersGroupByTheSameLocationModal'

export default {
  name: 'googleMap',
  inject: [
    '$utils',
    '$api',
    '$apiError'
  ],
  props: {
    playersProp: {
      type: Array,
      default () { return [] }
    },
    accessorComponentProp: {
      type: String,
      default () { return 'monitoring' }
    },
    filterPlayerByIconColorProp: {
      default () { return null }
    },
    playersAddressChangedProp: {
      default () { return { ref: null, player: null } }
    }
  },
  watch: {
    playersProp: {
      immediate: true,
      deep: true,
      async handler (newVal) {
        const playerWithGeoInfo = this.filterPlayers(newVal)
        this.players = playerWithGeoInfo
        await this.fitBoundsToMarkers({ markers: playerWithGeoInfo, setZoom: false })
        await this.setMarkersData({ players: this.players })
        this.clearPlayersPreview()
        await this.autoSelectIfOneVisibleMarker({ onPlayersChanged: true })
      }
    },
    accessorComponentProp (newVal) {
      this.accessorComponent = newVal
    },
    filterPlayerByIconColorProp (newVal) {
      this.clearAllInfoWIndow()
      this.clearMarkersModalGroupByTheSameLocation()
      this.clearPlayersPreview()
      this.filterPlayerByIconColor = newVal
      this.markers = this.filterMarkers({ markers: this.markers })
      this.$nextTick(async (_) => { await this.autoSelectIfOneVisibleMarker({ filterPlayerByIconColorChanged: true }) })
    },
    async playersAddressChangedProp (newVal) {
      if (newVal.player && newVal.ref) {
        await this.handlePlayersAddressChanged(newVal.player)
      }
    },
    markers: {
      deep: true,
      async handler (newVal) {
        if (this.debouncedReaddMarkers) {
          await this.debouncedReaddMarkers(newVal)
        }
      }
    }
  },
  data () {
    return {
      players: [],
      map: shallowRef(null),
      mapOverlayView: null,
      currentZoom: null,
      coordInfoWindow: null,
      center: { lat: 10.3343, lng: 123.89279 },
      defaultZoom: 3,
      maxZoom: 17,
      accessorComponent: this.accessorComponentProp,
      markers: [],
      enableMarkerInfoDialog: false,
      selectedMarker: null,
      visibleMarkers: {},
      infoWindow: [],
      mapWrapperPosition: {
        startXPosition: 0,
        startYPosition: 0,
        maxYPosition: 0,
        maxXPosition: 0
      },
      filterPlayerByIconColor: null,
      actionsOnBounceChanged: {
        showModalForMarkersGroupByTheSameLocation: null,
        showPlayersPreview: null,
        selectMarker: null,
        triggerMarkerToSelected: null
      },
      markerClickedEvent: null,
      tagSliderExpanded: false
    }
  },
  mixins: [],
  methods: {
    /**
     * Reset and add all the markers on the map and add fresh set of markers again.
     * NOTE: Practically debounced version will be callsed, see `mounted()` for details.
     */
    async readdMarkers (markers) {
      const map = toRaw(this.map)
      await Markers.reset()
      for (const markerData of markers) {
        // NOTE: create Google Map AdvancedMarkerElement and add listener right away
        const marker = await Markers.addMarker(map, markerData)
        marker.addListener(
          'click',
          (event) => {
            this.handleMarkerChange({
              action: 'MARKER_CLICKED',
              payload: {
                data: {
                  markerData,
                  marker,
                  domEvent: event?.domEvent ?? false
                }
              }
            })
          }
        )
      }
    },
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('google-map-change', { action, payload: { data } })
      }
    },
    async initMap () {
      const { event } = await google.maps.importLibrary('core')
      const { Map } = await google.maps.importLibrary('maps')
      this.currentZoom = this.defaultZoom
      this.map = new Map(document.getElementById('googleMap'), {
        zoom: this.defaultZoom,
        center: this.center,
        mapId: '2347e0f1a1e7e228',
        disableDefaultUI: true,
        zoomControl: true,
        maxZoom: this.maxZoom,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        latLngBounds: {
          north: 0,
          south: 0,
          east: 0,
          west: 0
        }
      })
      const map = toRaw(this.map)

      await this.setSearchBar()

      await this.setMarkersData({ players: this.players })

      // Zoom changed listener
      map.addListener('zoom_changed', async () => {
        this.markers = []
        this.currentZoom = map.getZoom()
        await this.setMarkersData({ players: this.players })
        await this.setVisibleMarkers()
        this.clearAllInfoWIndow()
        this.clearMarkersModalGroupByTheSameLocation()
        this.clearPlayersPreview()
        this.markers = this.filterMarkers({ markers: this.markers })
      })

      // Bounds changed listener
      map.addListener('bounds_changed', () => {
        const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
        // Show Modal of markers with the same location
        if (actionsOnBounceChanged.showModalForMarkersGroupByTheSameLocation) {
          const showModalForMarkersGroupByTheSameLocation = actionsOnBounceChanged.showModalForMarkersGroupByTheSameLocation
          const previousPlayersPreviewMrkrIdClicked = showModalForMarkersGroupByTheSameLocation.previousPlayersPreviewMrkrIdClicked

          this.showMarkersModalGroupByTheSameLocation(showModalForMarkersGroupByTheSameLocation)

          // Show Players Preview
          if (previousPlayersPreviewMrkrIdClicked) {
            const modalEl = $(`#${MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID}`)
            const modalElOffset = modalEl.offset()
            const selectedMarkereEl = $(`.mrkrId${previousPlayersPreviewMrkrIdClicked}`)
            const selectedMarkerOffset = selectedMarkereEl.offset()
            const mapPlayersPreviewModalEl = $(`#${PLAYERS_PREVIEW_MODAL_ID}`).find('.player-preview')
            const alwaysPositionOnTopOfIcons = true
            let modalStyle = {}

            // Position preview on top of selected icon
            const previewXPosition = (selectedMarkerOffset.left - (mapPlayersPreviewModalEl.width() / 2)) + 23
            const previewYPosition = selectedMarkerOffset.top - (mapPlayersPreviewModalEl.height() + 2)
            modalStyle = { left: `${previewXPosition}px`, top: `${previewYPosition}px` }
            // Position preview on top of all icons
            if (alwaysPositionOnTopOfIcons) {
              const modelElXCenter = modalElOffset.left + (modalEl.width() / 2)
              const previewXPosition = (modelElXCenter - (mapPlayersPreviewModalEl.width() / 2))
              const previewYPosition = modalElOffset.top - (mapPlayersPreviewModalEl.height() - 5)
              modalStyle = { left: `${previewXPosition}px`, top: `${previewYPosition}px` }
            }

            this.showPlayersPreviewModal({ modalStyle })
          }

          // Reset bounce action for show modal
          actionsOnBounceChanged.showModalForMarkersGroupByTheSameLocation = null
          this.actionsOnBounceChanged = actionsOnBounceChanged
          this.hideAllMarkers()
        }

        // Show Players Preview
        if (actionsOnBounceChanged.showPlayersPreview) {
          this.showPlayersPreviewModal(actionsOnBounceChanged.showPlayersPreview)
          actionsOnBounceChanged.showPlayersPreview = null
        }

        // Select specific marker
        if (actionsOnBounceChanged.selectMarker) {
          const selectMarker = actionsOnBounceChanged.selectMarker
          const targetMrkrId = selectMarker.targetMarker.id
          const targetMarkerComponent = Markers.getMarkers().filter(({ data }) => { return targetMrkrId === data.playersInfo._edit.uid })

          // Clear selected marker
          if (selectMarker.clearPreviousSelectedMarker) this.selectedMarker = null

          // Find as a single marker
          if (targetMarkerComponent.length && (this.selectedMarker?.markerData?.playersInfo?._edit?.uid !== targetMrkrId)) {
            event.trigger(targetMarkerComponent[0].marker, 'click')
            actionsOnBounceChanged.selectMarker = null
            this.actionsOnBounceChanged = actionsOnBounceChanged
          } else {
            const markersWithMembers = Markers.getMarkers().filter(({ data }) => { return data.members })
            let parentMarkerMapObj = null

            markersWithMembers.forEach((component) => {
              for (const mIndx in component.data.members) {
                const member = component.data.members[mIndx]
                if (member.playersInfo._edit.uid === targetMrkrId) {
                  parentMarkerMapObj = component.marker
                }
              }
            })

            if (parentMarkerMapObj && (this.selectedMarker && this.selectedMarker.data?.playersInfo?._edit?.uid !== targetMrkrId)) {
              event.trigger(parentMarkerMapObj, 'click')
              setTimeout(() => {
                $(`.same-geo-info-group-item-icon.mrkrId${targetMrkrId}`).find('.same-geo-info-group-item-icon-cover').trigger('click')
                actionsOnBounceChanged.selectMarker = null
                this.actionsOnBounceChanged = actionsOnBounceChanged
              }, 500)
            }
          }
        }
      })

      // Drag Start listener
      map.addListener('dragstart', async () => {
        await this.setVisibleMarkers()
        this.clearPlayersPreview()
        this.clearMarkersModalGroupByTheSameLocation()
      })

      // Drag End listener
      map.addListener('dragend', async () => {
        await this.setVisibleMarkers()
        this.showAllMarkers()
        this.markers = this.filterMarkers({ markers: this.markers })
      })

      // Set map overlay on projection changed
      map.addListener('projection_changed', async () => {
        const { OverlayView } = await google.maps.importLibrary('maps')

        this.mapOverlayView = new OverlayView()
        this.mapOverlayView.draw = () => {}
        this.mapOverlayView.setMap(map)
      })

      // Center changed listener
      map.addListener('center_changed', () => {

      })

      // Map Clicked
      map.addListener('click', async () => {
        this.clearPlayersPreview()
        this.clearMarkersModalGroupByTheSameLocation()
        this.showAllMarkers()
        this.unSelectAllMarkers()
        await this.setVisibleMarkers()
      })
      this.$nextTick(async () => {
        await this.fitBoundsToMarkers({ markers: this.filterPlayers(this.playersProp), setZoom: true })
      })
    },
    async handlePlayersAddressChanged (targetPlayer) {
      const map = toRaw(this.map)
      const { LatLng } = await google.maps.importLibrary('core')

      this.$utils.mainLoader(true)
      const targetMarkerComponent = Markers.getMarkers().filter(({ data }) => { return targetPlayer.serialNumber === data.playersInfo.serialNumber })
      const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)

      // Find in single markers
      if (targetMarkerComponent.length) {
        const markerData = targetMarkerComponent[0].data
        actionsOnBounceChanged.selectMarker = { targetMarker: targetPlayer, ref: this.$utils.generateUniqueID() }
        this.actionsOnBounceChanged = actionsOnBounceChanged
        map.setCenter(
          new LatLng(markerData.lat, markerData.lng),
          17
        )
      } else {
        const markersWithMembers = Markers.getMarkers().filter(({ data }) => { return data.members })
        let position = null
        markersWithMembers.forEach((component) => {
          for (const mIndx in component.data.members) {
            const member = component.data.members[mIndx]

            if (member.playersInfo.serialNumber === targetPlayer.serialNumber) {
              position = new LatLng(component.data.lat, component.data.lng)
            }
          }
        })

        if (position) {
          actionsOnBounceChanged.selectMarker = { targetMarker: targetPlayer, ref: this.$utils.generateUniqueID() }
          this.actionsOnBounceChanged = actionsOnBounceChanged
          map.setCenter(position, 17)
        }
      }

      this.$utils.mainLoader(false)
    },
    isMarkerHasMembers (marker) {
      return (marker.members && marker.members.length > 0)
    },
    async autoSelectIfOneVisibleMarker (params) {
      const { event, LatLng } = await google.maps.importLibrary('core')

      if (!params) params = {}
      const visibleIcons = this.markers.filter((marker) => { return !marker.hideIcon })

      // On component mount or players list changed
      if (params.onPlayersChanged || params.onMount) {
        const runAutoSelect = visibleIcons.length === 1 && !this.isMarkerHasMembers(visibleIcons[0])

        if (runAutoSelect) {
          // NOTE: find first visible marker and then click on it
          const targetMarker = Markers.getMarkers().find(({ data }) => data.hideIcon === false)
          if (targetMarker) {
            // Trigger click for single marker
            const { marker: markerObjInMap, data: markerData } = targetMarker
            if (!this.isMarkerHasMembers(markerData)) {
              event.trigger(markerObjInMap, 'click')
            }

            if (this.isMarkerHasMembers(markerData) && this.filterPlayerByIconColor) {
              event.trigger(markerObjInMap, 'click')

              setTimeout(() => {
                $('.same-geo-info-group-item-icon').each((indx, el) => {
                  if (indx === 0) $(el).find('.same-geo-info-group-item-icon-cover').trigger('click')
                })
              }, 500)
            }
          }
        }
      }

      // Filter by color changed
      if (params.filterPlayerByIconColorChanged && visibleIcons.length === 1) {
        const targetMarker = visibleIcons[0]
        const getTargetMarkerObj = Markers.getMarkers().filter(({ data }) => { return data.playersInfo._edit.uid === targetMarker.id })
        const markerObjInMap = getTargetMarkerObj[0] ? getTargetMarkerObj[0].marker : null
        const triggerClick = { run: false, markerIdInsideModal: null }

        // Auto click if single marker
        if (!this.isMarkerHasMembers(targetMarker)) {
          triggerClick.run = true
          triggerClick.markerIdInsideModal = null
        }

        if (this.isMarkerHasMembers(targetMarker)) {
          const findMatchFromMembers = targetMarker.members.filter((m) => { return this.filterPlayerByIconColor === m.playersInfo._edit.status.iconColor })
          const isParentMarkerMatch = targetMarker.playersInfo._edit.status.iconColor === this.filterPlayerByIconColor

          // Auto click if only parent marker is the only result of filter
          if (findMatchFromMembers.length < 1 && isParentMarkerMatch) {
            triggerClick.run = true
            triggerClick.markerIdInsideModal = targetMarker.id
          }

          // Turn off trigger click, instead Auto center the parent marker and click the icon inside modal
          if (!isParentMarkerMatch && findMatchFromMembers.length === 1) {
            triggerClick.run = false

            const getMemberInfo = targetMarker.members.find((m) => { return this.filterPlayerByIconColor === m.playersInfo._edit.status.iconColor })
            const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
            actionsOnBounceChanged.selectMarker = { targetMarker: getMemberInfo, clearPreviousSelectedMarker: true, ref: this.$utils.generateUniqueID() }
            this.actionsOnBounceChanged = actionsOnBounceChanged
            toRaw(this.map).setCenter(new LatLng(targetMarker.lat, targetMarker.lng), this.map.getZoom())
          }
        }

        if (triggerClick.run) {
          const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
          setTimeout(async () => { // Added a timeout to make sure marker exist in the map
            if (markerObjInMap) event.trigger(markerObjInMap, 'click')
            actionsOnBounceChanged.selectMarker = { targetMarker, clearPreviousSelectedMarker: true, ref: this.$utils.generateUniqueID() }
            this.actionsOnBounceChanged = actionsOnBounceChanged
          }, 1000)
        }
      }
    },
    getMarkerIconStyleProp (params) {
      const colorGreen = '#7cc57c'
      const colorBlue = '#82a9f6'
      const colorWhite = '#ffffff'
      const iconProp = {
        textColor: colorWhite,
        fillColor: colorGreen,
        strokeColor: colorGreen,
        groupIcon: false,
        forGoogleMap: true,
        iconClassName: null
      }

      // Grouped marker
      if ((params.groupId && params.members) && params.members.length) {
        iconProp.groupIcon = true
        iconProp.textColor = colorWhite
        iconProp.text = (params.members.length + 1) // 1 as the parent

        const hasTheSameStatus = params.members.every((member) => {
          return member.playersInfo._edit.status.status === params.playersInfo._edit.status.status
        })

        if (hasTheSameStatus) {
          iconProp.fillColor = params.playersInfo._edit.status.iconStyleProp.fillColor
          iconProp.strokeColor = params.playersInfo._edit.status.iconStyleProp.strokeColor
          iconProp.iconClassName = params.playersInfo._edit.status.iconClassName
        } else {
          iconProp.fillColor = colorBlue
          iconProp.strokeColor = colorBlue
        }
      } else if (params._edit && params._edit.status) {
        iconProp.fillColor = params._edit.status.iconStyleProp.fillColor
        iconProp.strokeColor = params._edit.status.iconStyleProp.strokeColor
        iconProp.iconClassName = params._edit.status.iconClassName
      } else if (params.playersInfo && params.playersInfo._edit) {
        iconProp.fillColor = params.playersInfo._edit.status.iconStyleProp.fillColor
        iconProp.strokeColor = params.playersInfo._edit.status.iconStyleProp.strokeColor
        iconProp.iconClassName = params.playersInfo._edit.status.iconClassName
      }

      return iconProp
    },
    async getMarkerXYCoordinates (params) {
      const { LatLng, Point } = await google.maps.importLibrary('core')

      const marker = params.marker
      const zoom = params.zoom ? params.zoom : toRaw(this.map).getZoom()

      const getLatLong = new LatLng(marker.lat, marker.lng)
      const latLng = getLatLong
      const scale = 1 << zoom

      const worldCoordinate = await this.project(latLng)

      // Pixel coordinates from the map
      const pixelCoordinate = new Point(
        Math.floor(worldCoordinate.x * scale),
        Math.floor(worldCoordinate.y * scale)
      )

      // Pixel Coordinates inside div map container
      let pixelCoordinateInsideMapWrapper = null
      if (params.fromLatLngToContainerPixel) {
        pixelCoordinateInsideMapWrapper = this.mapOverlayView.getProjection().fromLatLngToContainerPixel(getLatLong)
      }

      // Tile coordinates
      const tileCoordinate = new Point(
        Math.floor((worldCoordinate.x * scale) / TILE_SIZE),
        Math.floor((worldCoordinate.y * scale) / TILE_SIZE)
      )

      return {
        content: [
          `Player: ${marker.playersInfo.name}`,
          `LatLng: ${latLng}`,
          `Zoom level: ${zoom}`,
          `World Coordinate: ${worldCoordinate}`,
          `Pixel Coordinate: ${pixelCoordinate}`,
          `Pixel Coordinate inside Map Wrapper: ${pixelCoordinateInsideMapWrapper}`,
          `Tile Coordinate: ${tileCoordinate}`
        ].join('<br>'),
        data: {
          latLng,
          zoomLevel: zoom,
          worldCoordinate,
          pixelCoordinate,
          pixelCoordinateInsideMapWrapper,
          tileCoordinate
        }
      }
    },
    setmapWrapperPosition () {
      const mapWrapperEl = $('#googleMap')
      if (mapWrapperEl.length === 0) return
      const startXPosition = mapWrapperEl.offset().left
      const startYPosition = mapWrapperEl.offset().top
      const maxXPosition = mapWrapperEl.offset().left + mapWrapperEl.width()
      // verticalOffset to compensate for control panel at the bottom right
      const maxYPosition = mapWrapperEl.offset().top + mapWrapperEl.height()

      // Adjust center Y position when tag slider is expanded
      // 15 and 55 are approx height of tag slider compressed and expanded respectively
      const verticalOffsetAdjustment = !this.tagSliderExpanded
        ? 15
        : 55

      this.mapWrapperPosition = {
        startXPosition,
        startYPosition,
        maxXPosition,
        maxYPosition,
        centerXPosition: maxXPosition - (mapWrapperEl.width() / 2),
        centerYPosition: maxYPosition - (mapWrapperEl.height() / 2) - verticalOffsetAdjustment
      }
    },
    checkIfExistInMarkersList (markerSrc, markersListSrc) {
      const markersList = this.$utils.clone(markersListSrc)
      const marker = this.$utils.clone(markerSrc)

      const res = {
        type: 'setAsMarker',
        exist: false,
        position: -1
      }

      for (const indx in markersList) {
        const markerLItem = markersList[indx]

        if (this.isMarkerHasMembers(markerLItem)) {
          const findPositionInMembers = markerLItem.members.findIndex((memberMarker) => { return memberMarker.id === marker.id })

          if (findPositionInMembers >= 0) {
            res.type = 'setAsMember'
            res.exist = true
            res.position = findPositionInMembers
            break
          }
        } else {
          if (markerLItem.id === marker.id) {
            res.exist = true
            res.position = indx
            break
          }
        }
      }

      return res
    },
    unSelectAllMarkers () {
      this.markers = this.filterMarkers({ markers: this.markers, unselectAllMarkers: true })
    },
    showAllMarkers () {
      const newMarkers = JSON.parse(JSON.stringify(this.markers))
      for (let i = 0; i < newMarkers.length; i++) {
        newMarkers[i].hideIcon = false
      }
      this.markers = newMarkers
    },
    hideAllMarkers () {
      const newMarkers = JSON.parse(JSON.stringify(this.markers))
      for (let i = 0; i < newMarkers.length; i++) {
        newMarkers[i].hideIcon = true
      }
      this.markers = newMarkers
    },
    filterMarkers (params) {
      const newMarkers = JSON.parse(JSON.stringify(params.markers))
      const filterPlayerByIconColor = this.filterPlayerByIconColor

      for (let i = 0; i < newMarkers.length; i++) {
        const marker = newMarkers[i]

        // Filter by color
        if (filterPlayerByIconColor) {
          marker.hideIcon = true
          let match = marker.playersInfo._edit.status.iconColor === filterPlayerByIconColor // Check parent marker match

          // If parent did not match then try in members
          if (!match && (this.isMarkerHasMembers(marker) && marker.members.find((i) => { return i.playersInfo._edit.status.iconColor === filterPlayerByIconColor }))) {
            match = true
          }

          if (match) {
            marker.hideIcon = false
            const targetKey = marker.parentAndMembersHasTheSameStatus ? 'default' : filterPlayerByIconColor

            marker.iconPropKey = targetKey
            marker.iconProp = marker.iconPropOptions[marker.iconPropKey]
          } else {
            marker.iconPropKey = 'default'
            marker.iconProp = marker.iconPropOptions[marker.iconPropKey]
          }
        } else {
          marker.hideIcon = false
          marker.iconPropKey = 'default'
          marker.iconProp = marker.iconPropOptions[marker.iconPropKey]
        }

        // Select a Marker. Update iconProp to adjust SVG style
        if (params.selectMarker) {
          if (marker.id === params.selectMarker.id && !this.isMarkerHasMembers(marker)) {
            const newIconProp = this.$utils.clone(marker.iconProp)
            newIconProp.backgroundWithOpacity = { fillColor: newIconProp.fillColor, opacity: '0.2' }
            marker.iconProp = newIconProp
            marker.hideIcon = false
          }
        }

        // Unselect all markers
        if (params.unselectAllMarkers) {
          const newIconProp = this.$utils.clone(marker.iconProp)
          newIconProp.backgroundWithOpacity = { fillColor: newIconProp.fillColor, opacity: '0.2' }
          marker.iconProp = newIconProp
        }
      }

      return newMarkers
    },
    async fitBoundsToMarkers (params = {}) {
      const map = toRaw(this.map)
      const { LatLngBounds, LatLng } = await google.maps.importLibrary('core')

      if (Array.isArray(params.markers) && !params.markers.length) return

      const bounds = new LatLngBounds()
      const dataSrc = params.markers

      if (Array.isArray(dataSrc) && dataSrc.length) {
        dataSrc.forEach((m) => {
          let position = null
          if (m.geoInfo && (m.geoInfo.latitude && m.geoInfo.longitude)) {
            position = new LatLng(m.geoInfo.latitude, m.geoInfo.longitude)
          } else if (m.lat && m.lng) {
            position = new LatLng(m.lat, m.lng)
          }

          bounds.extend(position)
        })
      } else {
        bounds.extend(new LatLng(dataSrc.lat, dataSrc.lng))
        // Members
        if (this.isMarkerHasMembers(dataSrc)) {
          dataSrc.members.forEach((m) => {
            const position = new LatLng(m.lat, m.lng)
            bounds.extend(position)
          })
        }
      }
      if (map) {
        map.fitBounds(bounds)
      }

      if (params.setZoom) {
        const listener = map.addListener('idle', () => {
          // this.map.setZoom(this.defaultZoom)
          listener.remove()
        })
      }
    },
    async setMarkersData (params) {
      const { InfoWindow } = await google.maps.importLibrary('streetView')
      const { LatLng } = await google.maps.importLibrary('core')

      let markers = []

      if (params.players) {
        // GROUP MARKERS BY THE SAME LATITUDE AND LONGITUDE
        for (const player of params.players) {
          if (player.geoInfo) {
            const newMarker = {
              lat: player.geoInfo.latitude,
              lng: player.geoInfo.longitude,
              status: player._edit.status.status,
              iconProp: this.getMarkerIconStyleProp(player),
              id: player._edit.uid,
              playersInfo: player,
              name: player.name
            }

            const xyCoordinates = await this.getMarkerXYCoordinates({ marker: newMarker })
            newMarker.xyCoordinates = xyCoordinates.data

            // Check if had the same lat and long in the list
            const theSameLocationIndx = markers.findIndex((m) => { return (m.lat === newMarker.lat && m.lng === newMarker.lng) })

            if (theSameLocationIndx >= 0) {
              const groupedItem = this.$utils.clone(markers[theSameLocationIndx])
              groupedItem.groupId = groupedItem.id
              groupedItem.groupByTheSameGeoInfo = true

              if (!this.isMarkerHasMembers(groupedItem)) groupedItem.members = []

              const newGroupMember = this.$utils.clone(newMarker)
              newGroupMember.groupId = groupedItem.groupId
              newGroupMember.groupType = 'groupByTheSameGeoInfo'
              delete newGroupMember.icon
              delete newGroupMember.xyCoordinates

              groupedItem.members.push(newGroupMember)

              groupedItem.iconProp = this.getMarkerIconStyleProp(groupedItem) // Update Icon

              markers[theSameLocationIndx] = groupedItem
            } else {
              markers.push(newMarker)
            }
          }
        }

        /*
            GROUP MARKERS BY SHORT DISTANCE
            - Get nearest markers with given x,y distance (in pixel) and convert into flat list as members
            - Get markers without nearest marker and confirm at the bottom part if really no markers owns him as nearest
           */
        const markersGroupByShortDistance = []
        const markersWithoutNearestMarker = []
        const findNearestMarker = (marker) => {
          return markers.filter((neighborMarker) => {
            const maxDistance = 58 // in pixel
            const neighborMarkerXCoordinate = neighborMarker.xyCoordinates.pixelCoordinate.x
            const neighborMarkerYCoordinate = neighborMarker.xyCoordinates.pixelCoordinate.y
            let xDifference = 0
            let yDifference = 0

            if (marker.id !== neighborMarker.id) {
              // Make sure no negative value for X difference
              if (neighborMarkerXCoordinate > marker.xyCoordinates.pixelCoordinate.x) {
                xDifference = neighborMarkerXCoordinate - marker.xyCoordinates.pixelCoordinate.x
              } else {
                xDifference = marker.xyCoordinates.pixelCoordinate.x - neighborMarkerXCoordinate
              }

              // Make sure no negative value for Y difference
              if (neighborMarkerYCoordinate > marker.xyCoordinates.pixelCoordinate.y) {
                yDifference = neighborMarkerYCoordinate - marker.xyCoordinates.pixelCoordinate.y
              } else {
                yDifference = marker.xyCoordinates.pixelCoordinate.y - neighborMarkerYCoordinate
              }

              if (xDifference <= maxDistance && yDifference <= maxDistance) {
                neighborMarker.shortDistance = { x: xDifference, y: yDifference }

                return true
              }

              return false
            }

            return false
          })
        }

        for (const marker of this.$utils.clone(markers)) {
          const nearestMarker = findNearestMarker(marker)
          if (nearestMarker.length) {
            // Create nearest markers as flat list
            const allNearestMarkersAsFlatList = () => {
              const allNearestLocList = []

              for (const alNMIndx in nearestMarker) {
                const nMItem = this.$utils.clone(nearestMarker[alNMIndx])
                const nMItemMembers = (this.isMarkerHasMembers(nMItem)) ? this.$utils.clone(nMItem.members) : null

                if (nMItem.groupId) delete nMItem.groupId
                if (nMItem.members) delete nMItem.members
                if (nMItem.groupByTheSameGeoInfo) delete nMItem.groupByTheSameGeoInfo

                allNearestLocList.push(nMItem)

                if (nMItemMembers) {
                  nMItemMembers.forEach((item) => {
                    if (item.groupId) delete item.groupId
                    if (item.members) delete item.members
                    if (item.groupByTheSameGeoInfo) delete item.groupByTheSameGeoInfo
                    allNearestLocList.push(item)
                  })
                }
              }

              return allNearestLocList
            }

            // Process new group of markers
            let newMarkerAsGroup = this.$utils.clone(marker)
            const checkIfExistInMarkersList = this.checkIfExistInMarkersList(newMarkerAsGroup, markersGroupByShortDistance)

            if (!checkIfExistInMarkersList.exist || (checkIfExistInMarkersList.exist && checkIfExistInMarkersList.type !== 'setAsMember')) {
              let newMarkerAsGroupPositionInList = checkIfExistInMarkersList.position

              if (newMarkerAsGroupPositionInList < 0) {
                newMarkerAsGroup.groupId = newMarkerAsGroup.id
                newMarkerAsGroup.groupByShortDistance = true

                markersGroupByShortDistance.push(newMarkerAsGroup)
                newMarkerAsGroupPositionInList = markersGroupByShortDistance.length - 1
              }

              newMarkerAsGroup = markersGroupByShortDistance[newMarkerAsGroupPositionInList]

              if (!this.isMarkerHasMembers(newMarkerAsGroup)) newMarkerAsGroup.members = []

              // Add nearest markers if not exist in the list
              allNearestMarkersAsFlatList().forEach((item) => {
                const candItemExistInList = this.checkIfExistInMarkersList(item, markersGroupByShortDistance)
                if (!candItemExistInList.exist) {
                  item.groupId = newMarkerAsGroup.groupId
                  item.groupType = 'groupByShortDistance'
                  newMarkerAsGroup.members.push(item)
                }
              })

              newMarkerAsGroup.iconProp = this.getMarkerIconStyleProp(newMarkerAsGroup) // create icon
            }
          } else {
            markersWithoutNearestMarker.push(marker)
          }

          // Show marker info dialog
          if (this.enableMarkerInfoDialog) {
            const markerCoordinates = await this.getMarkerXYCoordinates({ marker })
            const coordInfoWindow = new InfoWindow()
            const content = markerCoordinates.content
            const getLatLong = new LatLng(marker.lat, marker.lng)
            coordInfoWindow.setContent(content)
            coordInfoWindow.setPosition(getLatLong)
            coordInfoWindow.open(toRaw(this.map))
          }
        }

        markers = markersGroupByShortDistance
        markersWithoutNearestMarker.forEach((mWNMItem) => {
          const checkIfExistInMarkersList = this.checkIfExistInMarkersList(mWNMItem, markers)

          if (!checkIfExistInMarkersList.exist) {
            const newMarker = this.$utils.clone(mWNMItem)

            // Make sure members are not in the list
            if (this.isMarkerHasMembers(newMarker)) {
              newMarker.members = newMarker.members.filter((member) => {
                return !this.checkIfExistInMarkersList(member, markers).exist
              })
            }
            markers.push(newMarker)
          }
        })

        /**
           * Create icon props that will be use for:
           * - Filter player by status
           * - Selected player
           */
        markers.forEach((marker) => {
          marker.iconPropOptions = {
            default: marker.iconProp
          }
          marker.iconPropKey = 'default'

          if (this.isMarkerHasMembers(marker)) {
            marker.parentAndMembersHasTheSameStatus = marker.members.every((i) => { return i.status === marker.status })

            if (!marker.parentAndMembersHasTheSameStatus) {
              // Create icon prop for each member
              for (const i in marker.members) {
                const member = marker.members[i]
                const newiIconPropOption = this.$utils.clone(member.iconProp)
                newiIconPropOption.text = marker.members.filter((i) => { return i.status === member.status }).length
                newiIconPropOption.groupIcon = true

                // Add into options if not yet exist
                if (!marker.iconPropOptions[member.playersInfo._edit.status.iconColor]) {
                  if (marker.playersInfo._edit.status.iconColor === member.playersInfo._edit.status.iconColor) newiIconPropOption.text += 1 // Include parent in count if same status
                  if (newiIconPropOption.text <= 1) newiIconPropOption.groupIcon = false // Set groupIcon to false if text is less than 1

                  marker.iconPropOptions[member.playersInfo._edit.status.iconColor] = newiIconPropOption
                }
              }

              // Add parent icon prop if not exist in options
              if (!marker.iconPropOptions[marker.playersInfo._edit.status.iconColor]) {
                const asSingleMarker = this.$utils.clone(marker)
                delete asSingleMarker.groupId
                delete asSingleMarker.members
                const iconProp = this.getMarkerIconStyleProp(asSingleMarker) // create icon
                iconProp.text = 1
                iconProp.groupIcon = false

                marker.iconPropOptions[marker.playersInfo._edit.status.iconColor] = iconProp
              }
            }
          } else {
            marker.iconPropOptions[marker.playersInfo._edit.status.iconColor] = marker.iconProp
          }
        })

        this.markers = this.filterMarkers({ markers })
      }
    },
    async project (latLng) {
      const { Point } = await google.maps.importLibrary('core')
      let siny = Math.sin((latLng.lat() * Math.PI) / 180)
      // Truncating to 0.9999 effectively limits latitude to 89.189. This is
      // about a third of a tile past the edge of the world tile.
      siny = Math.min(Math.max(siny, -0.9999), 0.9999)

      return new Point(
        TILE_SIZE * (0.5 + latLng.lng() / 360),
        TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
      )
    },
    async setSearchBar () {
      this.searchInputEL = document.getElementById('monitoringSearchPlacesInput')

      // const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')
      const { SearchBox } = await google.maps.importLibrary('places')
      this.searchBox = new SearchBox(this.searchInputEL)
      this.searchBox.addListener('places_changed', async () => {
        this.handleSearchPlacesChanged()
        await this.setMarkersData({ players: this.players })
        this.selectedMarker = null
      })
    },
    async handleSearchPlacesChanged () {
      const map = toRaw(this.map)
      const { LatLngBounds } = await google.maps.importLibrary('core')
      this.$utils.mainLoader(true)
      const places = this.searchBox.getPlaces()

      if (places.length === 0) {
        this.$utils.mainLoader(false)
        return
      }

      // Get Players
      if (places[0].name) {
        const bounds = new LatLngBounds()

        places.forEach((place) => {
          if (!place.geometry) return // return if no geometry

          if (place.geometry.viewport) {
            // Only geocodes have viewport.
            bounds.union(place.geometry.viewport)
          } else {
            bounds.extend(place.geometry.location)
          }
        })

        map.fitBounds(bounds)

        map.setCenter({
          lat: places[0].geometry.location.lat(),
          lng: places[0].geometry.location.lng()
        })
        this.setVisibleMarkers()
        this.$utils.mainLoader(false)
      } else {
        this.$utils.mainLoader(false)
      }
    },

    async setVisibleMarkers () {
      const { LatLng } = await google.maps.importLibrary('core')

      const bounds = toRaw(this.map).getBounds()
      const markers = this.$utils.clone(this.markers)
      const visMarkers = []

      for (let i = 0; i < markers.length; i++) {
        const marker = markers[i]

        const position = new LatLng(marker.lat, marker.lng)

        if (bounds.contains(position) === true) {
          visMarkers.push(marker)
        }
      }

      // Create Summary
      const visibleMarkers = this.$utils.clone(this.visibleMarkers)
      visibleMarkers.visMarkers = visMarkers
      visibleMarkers.players = { list: [], iconProp: {} }
      visibleMarkers.playerGroupByStatus = {}
      visibleMarkers.playerGroupByColor = {} // Should be green, orange and red

      if (visMarkers.length) {
        // Get all players as list
        visMarkers.forEach((m) => {
          // Split members
          if (m.groupId && this.isMarkerHasMembers(m)) {
            const groupedMarker = this.$utils.clone(m)
            const memberMarker = this.$utils.clone(m.members)

            delete groupedMarker.members
            visibleMarkers.players.list.push(groupedMarker)

            // Get members
            for (const mMIndx in memberMarker) {
              visibleMarkers.players.list.push(memberMarker[mMIndx])
            }
          } else {
            visibleMarkers.players.list.push(m)
          }
        })

        // Get player summary icon prop
        const visibleMarkersPlayers = this.$utils.clone(visibleMarkers.players.list)
        const createMarkerIconParams = this.$utils.clone(visibleMarkersPlayers[0])
        createMarkerIconParams.groupId = this.$utils.generateUniqueID()
        createMarkerIconParams.members = (visibleMarkersPlayers.length > 1) ? visibleMarkersPlayers.filter((m, i) => { return i > 0 }) : undefined

        visibleMarkers.players.iconProp = this.getMarkerIconStyleProp(createMarkerIconParams)

        // Create status summary
        visibleMarkers.players.list.forEach(marker => {
          const getStatus = marker.playersInfo._edit.status.status
          const getColorIcon = marker.playersInfo._edit.status.iconColor

          // Group Player by status
          if (!visibleMarkers.playerGroupByStatus[getStatus]) {
            visibleMarkers.playerGroupByStatus[getStatus] = {
              iconProp: this.getMarkerIconStyleProp(marker.playersInfo),
              players: []
            }
          }
          visibleMarkers.playerGroupByStatus[getStatus].players.push(marker)

          // Group Player by status color
          if (!visibleMarkers.playerGroupByColor[getColorIcon]) {
            visibleMarkers.playerGroupByColor[getColorIcon] = {
              iconProp: this.getMarkerIconStyleProp(marker.playersInfo),
              players: []
            }
          }

          visibleMarkers.playerGroupByColor[getColorIcon].players.push(marker)
        })
      }

      this.visibleMarkers = visibleMarkers

      this.$nextTick(_ => {
        this.emitEvent('VISIBLE_MARKERS_CHANGED', { markers: this.markers, visibleMarkers: this.visibleMarkers })
      })
    },
    getVisibleMarkersGroupByTheSameLocation (params) {
      const visibleMarkers = {}
      visibleMarkers.visMarkers = params.marker
      visibleMarkers.players = { list: [], iconProp: {} }
      visibleMarkers.playerGroupByStatus = {}
      visibleMarkers.playerGroupByColor = {} // Should be green, orange and red

      // Get all players as list
      const groupedMarker = this.$utils.clone(params.marker)
      const memberMarker = this.$utils.clone(groupedMarker.members)
      delete groupedMarker.members
      visibleMarkers.players.list.push(groupedMarker)
      for (const mMIndx in memberMarker) visibleMarkers.players.list.push(memberMarker[mMIndx])

      // Get player summary icon prop
      const visibleMarkersPlayers = this.$utils.clone(visibleMarkers.players.list)
      const createMarkerIconParams = this.$utils.clone(visibleMarkersPlayers[0])
      createMarkerIconParams.groupId = this.$utils.generateUniqueID()
      createMarkerIconParams.members = (visibleMarkersPlayers.length > 1) ? visibleMarkersPlayers.filter((m, i) => { return i > 0 }) : undefined

      visibleMarkers.players.iconProp = this.getMarkerIconStyleProp(createMarkerIconParams)

      // Create status summary
      visibleMarkers.players.list.forEach(marker => {
        const getStatus = marker.playersInfo._edit.status.status
        const getColorIcon = marker.playersInfo._edit.status.iconColor

        // Group Player by status
        if (!visibleMarkers.playerGroupByStatus[getStatus]) {
          visibleMarkers.playerGroupByStatus[getStatus] = {
            iconProp: this.getMarkerIconStyleProp(marker.playersInfo),
            players: []
          }
        }
        visibleMarkers.playerGroupByStatus[getStatus].players.push(marker)

        // Group Player by status color
        if (!visibleMarkers.playerGroupByColor[getColorIcon]) {
          visibleMarkers.playerGroupByColor[getColorIcon] = {
            iconProp: this.getMarkerIconStyleProp(marker.playersInfo),
            players: []
          }
        }

        visibleMarkers.playerGroupByColor[getColorIcon].players.push(marker)
      })

      return visibleMarkers
    },
    async handleMarkerChange ($event) {
      if ($event.action === 'MARKER_CLICKED') {
        this.markerClickedEvent = $event

        // Update pixel Coordinate Inside Map Wrapper
        const payloadData = $event.payload.data
        const xyCoordinates = await this.getMarkerXYCoordinates({
          marker: payloadData.markerData,
          fromLatLngToContainerPixel: true
        })
        payloadData.markerData.xyCoordinates.pixelCoordinateInsideMapWrapper = xyCoordinates.data.pixelCoordinateInsideMapWrapper

        this.selectedMarker = payloadData
        const targetMarker = this.$utils.clone(payloadData.markerData)
        const groupByTheSameGeoInfo = targetMarker.groupByTheSameGeoInfo && !targetMarker.groupByShortDistance
        const groupByShortDistance = !targetMarker.groupByTheSameGeoInfo && targetMarker.groupByShortDistance
        const groupByShortDistanceAndTheSameGeoInfo = targetMarker.groupByTheSameGeoInfo && targetMarker.groupByShortDistance

        this.unSelectAllMarkers()

        if (targetMarker.groupId) { // Grouped marker
          // Group by the same location
          if (groupByTheSameGeoInfo) {
            await this.createModalMarkersGroupByTheSameLocation()
            this.emitEvent('MARKER_GROUP_BY_THE_SAME_LOCATION_CLICKED', { ...this.getVisibleMarkersGroupByTheSameLocation({ marker: targetMarker }), markers: [targetMarker] })
          }

          // Group by short distance only OR short distance and with the same location
          if (groupByShortDistance || groupByShortDistanceAndTheSameGeoInfo) {
            if (toRaw(this.map).getZoom() >= this.maxZoom) {
              await this.createModalMarkersGroupByTheSameLocation()
              this.emitEvent('MARKER_GROUP_BY_THE_SAME_LOCATION_CLICKED', { ...this.getVisibleMarkersGroupByTheSameLocation({ marker: targetMarker }), markers: [targetMarker] })
            } else if (this.filterPlayerByIconColor) {
              const findMatchFromMembers = targetMarker.members.filter((m) => { return this.filterPlayerByIconColor === m.playersInfo._edit.status.iconColor })
              const isParentMarkerMatch = targetMarker.playersInfo._edit.status.iconColor === this.filterPlayerByIconColor
              let countMatch = findMatchFromMembers.length
              if (isParentMarkerMatch) countMatch += 1

              if (countMatch > 1) {
                this.fitBoundsToMarkers({ markers: targetMarker })
              } else if (countMatch === 1) {
                let markerIdInsideModal = targetMarker

                if (findMatchFromMembers.length === 1) {
                  markerIdInsideModal = findMatchFromMembers[0]
                }

                const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
                actionsOnBounceChanged.showPlayersPreview = null
                actionsOnBounceChanged.selectMarker = { targetMarker: markerIdInsideModal, clearPreviousSelectedMarker: true, ref: this.$utils.generateUniqueID() }
                this.actionsOnBounceChanged = actionsOnBounceChanged

                this.fitBoundsToMarkers({ markers: targetMarker })
              }
            } else {
              this.fitBoundsToMarkers({ markers: targetMarker })
            }
          }
        } else { // Single marker
          window.setTimeout(() => {
            this.createPlayersPreviewModal({
              marker: $event.payload.data,
              domEvent: $event.payload.data.domEvent
            })
            this.markers = this.filterMarkers({ markers: this.markers, selectMarker: $event.payload.data.markerData })
          }, 2)
        }
      }
    },
    async getPlayersPreviewData (player, callback) {
      const apiRequest = async () => {
        this.$utils.mainLoader(true)
        const params = { width: '512', use_qsa: 1 }
        this.$api.get(`${CONFIG.API_BASE_URL}/player/${player.serialNumber}/info`, { params })
          .then(async (response) => {
            if (callback) {
              await callback(response.data)
            } else {
              this.mainLoader(false)
            }
          }).catch((error) => {
            this.error = error
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })

            if (!error?.response || !error.response?.status || error.response.status === 400) {
              this.emitEvent('RESET_SCHEDULE_ITEM_SELECTION', this.scheduleItemOrig)
            }
          })
      }

      await apiRequest()
    },
    clearAllInfoWIndow () {
      for (let i = 0; i < this.infoWindow.length; i++) {
        this.infoWindow[i].close()
      }
    },
    clearMarkersModalGroupByTheSameLocation () {
      if (document.getElementById(MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID)) {
        document.body.removeChild(document.getElementById(MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID))
      }
    },
    clearPlayersPreview () {
      if (document.getElementById(PLAYERS_PREVIEW_MODAL_ID)) {
        document.body.removeChild(document.getElementById(PLAYERS_PREVIEW_MODAL_ID))
      }
      this.emitEvent('PLAYERS_PREVIEW_CLOSED', { markers: this.markers, visibleMarkers: this.visibleMarkers })
    },
    showPlayersPreviewModal (params = {}) {
      const modalEl = $('#mapPlayersPreviewModal')

      if (params.modalStyle) {
        modalEl.css(params.modalStyle)
      }

      modalEl.addClass('show')
    },
    async createPlayersPreviewModal (params = {}) {
      const { LatLng } = await google.maps.importLibrary('core')

      this.clearPlayersPreview()

      if (!params.marker) return

      const getPreviewSizeAndIframeEl = async (schedule, markerId) => {
        // no wider/toller than 300px
        const threshouldDimension = 180
        const res = {
          // Iframe size on large screen for horizontal orientation
          size: {
            width: 332,
            height: 188
          },
          iframe: ''
        }

        // Iframe size on small screen for horizontal orientation
        if (this.$utils.is1280View()) {
          res.size = {
            width: 284,
            height: 161
          }
        }

        // Iframe size on large and small screen for vertical orientation
        if (schedule.dir && schedule.dir === 'vertical') {
          res.size.width = 160
          res.size.height = 284

          if (this.$utils.is1280View()) {
            res.size.width = 100
            res.size.height = 177
          }
        }

        /**
         * Calculate LED preview if player has led tag
         */
        const playersInfo = !params.groupMember ? params.marker.markerData.playersInfo : params.marker.playersInfo
        const ledTag = playersInfo.tags ? playersInfo.tags.find(tag => tag.startsWith('LED:')) : null
        let url = schedule.preview.url

        const fitDimensions = (width, height, threshouldDimension) => {
          const ratio = width / height
          if (width > height && width > threshouldDimension) {
            return [threshouldDimension, Math.floor(threshouldDimension / ratio)]
          }
          if (height > width && height > threshouldDimension) {
            // 16 pixels is a magic number, there's something with rounding
            return [Math.floor(threshouldDimension * ratio), threshouldDimension - 8]
          }

          return [width, height]
        }

        if (ledTag) {
          const splitVal = ledTag.substr(4).split('x')
          const contentWidth = parseInt(splitVal[0], 10)
          const contentHeight = parseInt(splitVal[1], 10)

          const [cardWidth, cardHeight] = fitDimensions(contentWidth, contentHeight, threshouldDimension)

          // fit height to the
          res.size.width = cardWidth
          res.size.height = cardHeight

          // add dimension parameters if there's any
          url += `&dimensions=${splitVal[0]}x${splitVal[1]}`
        }

        res.iframe = `<iframe style="${`width: ${res.size.width}px; height: ${res.size.height}px;`}" class="player-preview" id="playerPreview_${markerId}" src="${url}"></iframe>`

        return res
      }

      // Get Preview Details
      const targetMarker = params.marker
      const playersInfo = !params.groupMember ? targetMarker.markerData.playersInfo : targetMarker.playersInfo
      const markerId = !params.groupMember ? targetMarker.markerData.id : targetMarker.id

      await this.getPlayersPreviewData(playersInfo, async (previewData) => {
        const previewSizeAndIFrameEl = await getPreviewSizeAndIframeEl(previewData, markerId)
        let hasPreview = true

        if (!['ok', 'publish', 'online'].includes(playersInfo.status)) {
          hasPreview = false
        }

        if (playersInfo.degraded || playersInfo.configPending) {
          hasPreview = false
        }

        this.selectedMarker.previewData = previewData
        let previewXPosition = 0
        let previewYPosition = 0

        // Get center of the parent marker
        if (params.domEvent) {
          previewXPosition = (params.domEvent.clientX - params.domEvent.offsetX) + 23
          previewYPosition = (params.domEvent.clientY - params.domEvent.offsetY)
        }

        // Adjust modal position to center/top of the parent marker
        previewXPosition = previewXPosition - (previewSizeAndIFrameEl.size.width / 2)
        previewYPosition = previewYPosition - (previewSizeAndIFrameEl.size.height + 2)

        // Get TagSlider Height
        const tagSliderEl = $('#tagSlider')
        const tagSliderElHeight = tagSliderEl.length ? tagSliderEl.height() - 64 : 0
        // Create Preview with iframe
        if (hasPreview && (previewXPosition !== 0 && previewYPosition !== 0)) {
          // Add element modal to body
          let modalStyle = { left: `${previewXPosition}px`, top: `${previewYPosition - tagSliderElHeight}px`, minWidth: `${previewSizeAndIFrameEl.size.width}px`, minHeight: `${previewSizeAndIFrameEl.size.height}px` }
          $(`<div style="" id="${PLAYERS_PREVIEW_MODAL_ID}">
              <div id=${PLAYERS_PREVIEW_MODAL_CLOSE_ID} class="close icon"></div>
              <div class="iframe-wrapper">
                ${previewSizeAndIFrameEl.iframe}
              </div>
            </div>`)
            .appendTo('body')

          // Listen modal close event
          document.getElementById(`${PLAYERS_PREVIEW_MODAL_CLOSE_ID}`).addEventListener('click', () => {
            this.clearPlayersPreview()
          })

          // Re-center the map
          const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
          let getLatLongToCenterPosition = null

          if (params.groupMember) {
            getLatLongToCenterPosition = new LatLng(targetMarker.lat, targetMarker.lng)

            // Create a flag to show markers modal and preview modal to it's new position
            const modalEl = $(`#${MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID}`)
            const modalWidth = modalEl.width()
            const modalHeight = modalEl.height()
            const centerLeft = this.mapWrapperPosition.centerXPosition - (modalWidth / 2)
            const centerRight = this.mapWrapperPosition.centerYPosition - (modalHeight / 2)
            modalStyle = { left: `${centerLeft}px`, top: `${centerRight + tagSliderElHeight}px` }

            // Create a flag to show modal in center position after bounce changed
            actionsOnBounceChanged.showModalForMarkersGroupByTheSameLocation = { modalStyle, previousPlayersPreviewMrkrIdClicked: targetMarker.id }
            actionsOnBounceChanged.showPlayersPreview = null
          } else {
            getLatLongToCenterPosition = new LatLng(targetMarker.markerData.lat, targetMarker.markerData.lng)

            // Create a flag to show preview modal to it's new position
            const centerXPosition = this.mapWrapperPosition.centerXPosition - (previewSizeAndIFrameEl.size.width / 2)
            const centerYPosition = this.mapWrapperPosition.centerYPosition - (previewSizeAndIFrameEl.size.height + 16)
            modalStyle = { left: `${centerXPosition}px`, top: `${centerYPosition + tagSliderElHeight}px` }
            actionsOnBounceChanged.showPlayersPreview = { modalStyle }
          }

          // Re-center map
          toRaw(this.map).setCenter(getLatLongToCenterPosition, this.map.getZoom())

          // Add flag on what actions to do after map bounce in init initMap() bounds_changed listener
          this.actionsOnBounceChanged = actionsOnBounceChanged
        }

        // Send to parent component the selected marker with preview data
        let emitEventPayload = {
          markerData: this.$utils.clone(this.selectedMarker.markerData),
          previewData
        }

        if (params.groupMember && params.marker.groupType === 'groupByTheSameGeoInfo') {
          emitEventPayload = {
            markerData: this.$utils.clone(params.marker),
            previewData
          }
        }

        if (emitEventPayload.markerData.groupId) {
          delete emitEventPayload.markerData.groupId
        }

        this.emitEvent('MARKER_SELECTED', emitEventPayload)
        this.$utils.mainLoader(false)
      })
    },
    showMarkersModalGroupByTheSameLocation (params) {
      const modalEl = $(`#${MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID}`)
      if (params.modalStyle) modalEl.css(params.modalStyle)
      modalEl.removeClass('hide')
      modalEl.addClass('show')
    },
    async createModalMarkersGroupByTheSameLocation () {
      const { LatLng } = await google.maps.importLibrary('core')
      if (!this.selectedMarker) return

      // Hide all markers including parent marker
      this.hideAllMarkers()
      this.clearMarkersModalGroupByTheSameLocation()

      // Convert into flat list to show markers as list in modal
      const markerData = this.$utils.clone(this.selectedMarker.markerData)
      const master = markerData
      const currentMembers = markerData.members
      delete master.groupId
      delete master.members
      const asList = [master].concat(currentMembers)
      let markersIconEl = ''
      const markerIconList = []

      // Center last row in the list
      let iconsPerRow = 5
      const sevenPlayersPerRow = asList.length > 5 && asList.length <= 49
      const tenPlayersPerRow = asList.length > 49
      let iconWidth = 47
      let iconHeight = 47
      const iconLeftRightMargin = 5
      let countLastRowMarkers = asList.length % 5
      let addMarkerIconsClassname = 'five-icons-per-row'
      let targetElIndexToAdjustLeftMargin = null
      let calcElMarginLeft = null

      if (sevenPlayersPerRow) {
        iconsPerRow = 7
        iconWidth = 32
        iconHeight = 32
        countLastRowMarkers = asList.length % 7
        addMarkerIconsClassname = 'seven-icons-per-row'
      } else if (tenPlayersPerRow) {
        iconsPerRow = 10
        iconWidth = 21
        iconHeight = 21
        countLastRowMarkers = asList.length % 10
        addMarkerIconsClassname = 'ten-icons-per-row'
      }

      // Calculate left margin and target marker index to adjust last row into center position
      if ((sevenPlayersPerRow || tenPlayersPerRow) && countLastRowMarkers > 0) {
        targetElIndexToAdjustLeftMargin = (asList.length - countLastRowMarkers) // Terget icon index to add left margin
        calcElMarginLeft = ((iconsPerRow - countLastRowMarkers) * iconWidth) / 2
        calcElMarginLeft = calcElMarginLeft + ((iconsPerRow - countLastRowMarkers) * iconLeftRightMargin) / 2
      }

      // Create icon elements
      asList.forEach((item, indx) => {
        const addToList = !((this.filterPlayerByIconColor && item.playersInfo._edit.status.iconColor !== this.filterPlayerByIconColor))

        if (addToList) {
          const iconProp = this.getMarkerIconStyleProp(item.playersInfo)
          iconProp.width = '100%'
          iconProp.height = '100%'
          iconProp.status = item.playersInfo ? item.playersInfo._edit.status.status : item.status
          iconProp.iconClassName = item.playersInfo ? item.playersInfo._edit.status.iconClassName : item.iconClassName
          iconProp.backgroundWithOpacity = { fillColor: iconProp.strokeColor, opacity: '0.2' }

          const defaultIconUrl = getCustomIconUrl(iconProp)
          let marginLeft = '0px'

          // Adjust left margin to move last row into center position
          if (targetElIndexToAdjustLeftMargin !== null && targetElIndexToAdjustLeftMargin === indx) {
            marginLeft = `${calcElMarginLeft}px`
          }

          markersIconEl += `<div style="width: ${iconWidth}px; height: ${iconHeight}px; margin-left: ${marginLeft}" class="same-geo-info-group-item-icon parentMrkrId${markerData.id} mrkrId${item.id}">
              <iframe class="status-icon default-icon" src="${defaultIconUrl}"></iframe>
              <div class="same-geo-info-group-item-icon-cover"></div>
            </div>`

          markerIconList.push({ item, iconPropInList: iconProp })
        }
      })

      const markerCoordinates = this.$utils.clone(this.selectedMarker.markerData.xyCoordinates.pixelCoordinateInsideMapWrapper)
      const markerXPosition = markerCoordinates.x + this.mapWrapperPosition.startXPosition
      const markerYPosition = markerCoordinates.y + this.mapWrapperPosition.startYPosition

      // Append modal to body with display none and show after position adjustments
      $(`<div style="" class="hide" id="${MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID}">
          <div class="marker-icons ${addMarkerIconsClassname}">${markersIconEl}</div>
        </div>`)
        .appendTo('body')

      // Adjust modal position at the center of parent marker
      const modalEl = $(`#${MARKERS_GROUP_BY_THE_SAME_LOCATION_MODAL_ID}`)
      const modalWidth = modalEl.width()
      const modalHeight = modalEl.height()
      const newModalXPosition = markerXPosition - (modalWidth / 2)
      const newModalYPosition = markerYPosition - (modalHeight / 2)

      // Move to center of the map if new position is closed to the border and modal will not fit
      let moveToCenter = false

      if ((newModalXPosition + modalWidth) > this.mapWrapperPosition.maxXPosition) {
        moveToCenter = true
      } else if (newModalXPosition < this.mapWrapperPosition.startXPosition) {
        moveToCenter = true
      }

      if ((newModalYPosition + modalHeight) > this.mapWrapperPosition.maxYPosition) {
        moveToCenter = true
      } else if (newModalYPosition < this.mapWrapperPosition.startYPosition) {
        moveToCenter = true
      }

      let modalStyle = { left: `${newModalXPosition}px`, top: `${newModalYPosition}px` }
      if (moveToCenter) {
        // Set map to bounce center
        const getLatLong = new LatLng(markerData.lat, markerData.lng)
        const map = toRaw(this.map)
        map.setCenter(getLatLong, map.getZoom())

        // Add a flag to show markers modal after bounce changed
        const centerXPosition = this.mapWrapperPosition.centerXPosition - (modalWidth / 2)
        const centerYPosition = this.mapWrapperPosition.centerYPosition - (modalHeight / 2)
        modalStyle = { left: `${centerXPosition}px`, top: `${centerYPosition}px` }

        // Set actions after bounce
        const actionsOnBounceChanged = this.$utils.clone(this.actionsOnBounceChanged)
        actionsOnBounceChanged.showModalForMarkersGroupByTheSameLocation = { modalStyle }
        this.actionsOnBounceChanged = actionsOnBounceChanged
      } else {
        this.showMarkersModalGroupByTheSameLocation({ modalStyle })
      }

      // Show players preview on icon clicked
      const markerIcons = document.querySelectorAll('.same-geo-info-group-item-icon-cover')
      markerIcons.forEach((mi) => {
        mi.addEventListener('click', (e, i) => {
          const parentDivEl = e.target.parentElement
          const getParentIdClass = parentDivEl.className.toString().match(/(\bparentMrkrId\S+\b)/ig)
          const parentMarkerId = getParentIdClass.length ? getParentIdClass[0].substr(12) : null
          const getMarkerIdClass = parentDivEl.className.toString().match(/(\bmrkrId\S+\b)/ig)
          const markerId = getMarkerIdClass.length ? getMarkerIdClass[0].substr(6) : null
          const getParentMarkerDetails = this.markers.find((m) => { return m.id === parentMarkerId })
          let targetMarker = getParentMarkerDetails?.members?.find((m) => { return m.id === markerId }) ?? null;

          // Parent Marker
          if (!targetMarker && markerId === parentMarkerId) {
            targetMarker = this.$utils.clone(getParentMarkerDetails)
            delete targetMarker.members
          }

          // Show the selected icon
          $('.same-geo-info-group-item-icon').each((indx, el) => {
            if ($(el).hasClass(`mrkrId${targetMarker.id}`)) {
              $(el).removeClass('less-opacity')
              $(el).addClass('selected')
            } else {
              $(el).removeClass('selected')
              $(el).addClass('less-opacity')
            }
          })

          if (targetMarker) this.createPlayersPreviewModal({ marker: targetMarker, groupMember: true, domEvent: e, alwaysPositionOnTopOfIcons: true })
        })
      })

      // Auto click if there's one icon in the list show players preview
      if (markerIconList.length === 1) {
        $(`.mrkrId${markerIconList[0].item.id}`).trigger('click')
      }
    },
    filterPlayers (playersSrc) {
      const players = []

      playersSrc.forEach((p) => {
        if (p.geoInfo && (p.geoInfo.latitude && p.geoInfo.longitude)) {
          players.push(p)
        }
      })

      return players
    },
    async tagSliderExpansionHandler () {
      if (this.markerClickedEvent && this.$route.name === 'monitoring') {
        $(window).trigger('resize')
        this.tagSliderExpanded = true
      }
    },
    async tagSliderCompressionHandler () {
      if (this.markerClickedEvent && this.$route.name === 'monitoring') {
        $(window).trigger('resize')
        this.tagSliderExpanded = false
      }
    },
    async updateMapAreaDimensions () {
      // 500 ms delay to wait for tag slider animation complete
      await new Promise(resolve => setTimeout(resolve, 500));
      this.setmapWrapperPosition();
    }
  },

  beforeUnmount () {
    this.clearAllInfoWIndow()
    this.clearMarkersModalGroupByTheSameLocation()
    this.clearPlayersPreview()
  },
  unmounted () {
    $(window).off('resize')
    EventBus.$off('EXPAND_TAG_SLIDER', this.tagSliderExpansionHandler)
    EventBus.$off('COMPRESS_TAG_SLIDER', this.tagSliderCompressionHandler)
  },
  mounted () {
    this.debouncedReaddMarkers = _.debounce(this.readdMarkers, 250)
    this.players = this.filterPlayers(this.playersProp)

    // promise to initialize google map
    this.initMap().then(async () => {
      this.clearPlayersPreview()
      await this.updateMapAreaDimensions();
      await this.autoSelectIfOneVisibleMarker({ onMount: true })
    })

    $(window).on('resize', _.throttle(() => {
      this.initMap().then(async () => {
        this.clearAllInfoWIndow()
        this.clearPlayersPreview()
        await this.updateMapAreaDimensions();
      })
    }, 100));

    // Listen tag slider expand event
    EventBus.$on('EXPAND_TAG_SLIDER', this.tagSliderExpansionHandler)

    // Listen tag slider compress event
    EventBus.$on('COMPRESS_TAG_SLIDER', this.tagSliderCompressionHandler)
  }
}
